import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal", "content", "title", "closeButton", "driveLink"];
  static values = { channel: String };

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  open(event) {
    event.preventDefault();
    this.previouslyFocused = document.activeElement;

    const url = event.currentTarget.dataset.canvasModalUrl;
    const name = event.currentTarget.dataset.canvasModalName;
    const driveFileId = event.currentTarget.dataset.canvasModalDriveFileId;
    const channel = this.channelValue;

    this.titleTarget.textContent = name || "Canvas";
    this.driveLinkTarget.href = url || "#";

    this.show();
    this._loadContent(channel, driveFileId);
  }

  _loadContent(channel, driveFileId) {
    if (!channel || !driveFileId) {
      this.contentTarget.innerHTML = `<div class="flex items-center justify-center py-16"><span class="text-gray-400">No preview available</span></div>`;
      return;
    }

    this.contentTarget.innerHTML = `<div class="flex items-center justify-center py-16"><span class="text-gray-400 text-sm">Loading…</span></div>`;

    const fetchUrl = `/slack_archive/channels/${encodeURIComponent(channel)}/canvas/${encodeURIComponent(driveFileId)}`;

    fetch(fetchUrl, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (data.content) {
          this.contentTarget.innerHTML = `<div class="px-6 py-5 prose-sm dark:prose-invert overflow-y-auto" style="max-height: 70vh;">${data.content}</div>`;
        } else {
          this.contentTarget.innerHTML = `<div class="flex items-center justify-center py-16"><span class="text-gray-400">No content available</span></div>`;
        }
      })
      .catch(() => {
        this.contentTarget.innerHTML = `<div class="flex items-center justify-center py-16"><span class="text-red-400 text-sm">Failed to load canvas content. <a href="${this.driveLinkTarget.href}" target="_blank" rel="noopener noreferrer" class="underline">Open in Google Drive</a></span></div>`;
      });
  }

  close() {
    this.modalTarget.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this.handleKeydown);
    this.contentTarget.innerHTML = "";
    this.titleTarget.textContent = "";

    if (this.previouslyFocused) {
      this.previouslyFocused.focus();
      this.previouslyFocused = null;
    }
  }

  closeOnBackdrop(event) {
    if (event.target === this.modalTarget) {
      this.close();
    }
  }

  // Private

  show() {
    this.modalTarget.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.handleKeydown);
    this.closeButtonTarget.focus();
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      return;
    }

    if (event.key === "Tab") {
      const focusableSelectors =
        'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
      const focusableElements = Array.from(
        this.modalTarget.querySelectorAll(focusableSelectors),
      ).filter((el) => !el.disabled && el.offsetParent !== null);

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }
  }
}
